import { 
  FeedbackInput, 
  FeedbackView,
  RecognitionFeedbackConfigView,
  RecognitionFeedbackConfigInput 
} from "@/models/preformanceManagement";
import request from "../../utils/request";
import { Result } from "@/models/general";

/**
 * Give feedback to another user
 * @param input - Feedback input data
 * @returns Result containing the created feedback
 */
export async function giveFeedback(input: FeedbackInput): Promise<Result<FeedbackView>> {
  const response = await request.post<Result<FeedbackView>>("Feedback/GiveFeedback", input);
  return response.data;
}

/**
 * Get all feedbacks given by a specific user
 * @param userId - The ID of the user who gave the feedbacks
 * @returns Result containing list of feedbacks given by the user
 */
export async function getFeedbacksGiven(userId: string): Promise<Result<FeedbackView[]>> {
  const response = await request.get<Result<FeedbackView[]>>(`Feedback/GetFeedbacksGiven/${userId}`);
  return response.data;
}

/**
 * Get all feedbacks received by a specific user
 * @param userId - The ID of the user who received the feedbacks
 * @returns Result containing list of feedbacks received by the user
 */
export async function getFeedbacksReceived(userId: string): Promise<Result<FeedbackView[]>> {
  const response = await request.get<Result<FeedbackView[]>>(`Feedback/GetFeedbacksReceived/${userId}`);
  return response.data;
}

/**
 * Get the most frequently used feedback messages by a user
 * @param userId - The ID of the user
 * @returns Result containing list of most used feedback messages
 */
export async function getMostUsedFeedbacks(userId: string): Promise<Result<string[]>> {
  const response = await request.get<Result<string[]>>(`Feedback/GetMostUsedFeedbacks/${userId}`);
  return response.data;
}

/**
 * Get all public feedbacks in the company
 * @returns Result containing list of public feedbacks
 */
export async function getPublicFeedbacks(): Promise<Result<FeedbackView[]>> {
  const response = await request.get<Result<FeedbackView[]>>("Feedback/GetPublicFeedbacks");
  return response.data;
}

/**
 * Get the recognition & feedback privacy configuration
 * @returns Result containing the configuration
 */
export async function getRecognitionFeedbackConfig(): Promise<Result<RecognitionFeedbackConfigView>> {
  const response = await request.get<Result<RecognitionFeedbackConfigView>>("Feedback/GetRecognitionFeedbackConfig");
  return response.data;
}

/**
 * Update the recognition & feedback privacy configuration (Admin only)
 * @param input - Configuration input
 * @returns Result containing the updated configuration
 */
export async function updateRecognitionFeedbackConfig(input: RecognitionFeedbackConfigInput): Promise<Result<RecognitionFeedbackConfigView>> {
  const response = await request.post<Result<RecognitionFeedbackConfigView>>("Feedback/UpdateRecognitionFeedbackConfig", input);
  return response.data;
}
